<script setup lang="ts">
import { reactive } from 'vue';
import { CoButton } from '@deputy/copilot2';
import TimePicker from './TimePicker.vue';
import Datepicker from 'vue3-datepicker';
import { getTranslations } from '@/lib/util/i18n';
import { isShiftOverlapping } from '@/lib/shifts/isShiftOverlapping';
import { dateToIsoDate, isoDateToSeconds } from '@/lib/util/dateTime';
import { useTimesheetStore } from '@/stores/timesheets';
import {
  isEndTimeOverlappingBreak,
  isStartTimeOverlappingBreak,
} from '@/lib/breaks/isBreakOverlapping';

const timesheets = useTimesheetStore();

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'edit-shift-form',
});

interface TimeProp {
  hour?: string;
  minute?: string;
  period?: string;
}

const props = defineProps<{
  firstTime: TimeProp;
  label: string;
  placeholder: string;
  shiftOverlapWarning: string;
  breakOverlapWarning?: string;
  editStartTime?: boolean;
  editShiftTime?: boolean;
  secondTime?: TimeProp;
  secondLabel?: string;
  secondPlaceholder?: string;
}>();

const state = reactive({
  firstTime: {
    hh: props.firstTime.hour,
    mm: props.firstTime.minute,
    a: props.firstTime.period?.toLowerCase(),
  } as any,
  secondTime: {
    hh: props.secondTime?.hour,
    mm: props.secondTime?.minute,
    a: props.secondTime?.period?.toLowerCase(),
  } as any,
  emptyField: false,
  firstDate: new Date(timesheets.getStartShiftTime()),
  secondDate: new Date(),
});

function shiftTimeISO(time: any, date: any) {
  return dateToIsoDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    time.data.HH,
    time.data.mm
  );
}

const emit = defineEmits<{
  (e: 'updateShift', shiftStartTime?: string, shiftEndTime?: string): void;
}>();
</script>

<template>
  <div class="edit-shift-form">
    <div>
      <TimePicker
        v-model="state.firstTime"
        :label="label"
        :placeholder="placeholder"
        ><div class="date-picker">
          <Datepicker
            inputFormat="dd MMM yyyy"
            v-model="state.firstDate"
            :upperLimit="new Date()"
            :lowerLimit="new Date(timesheets.getStartShiftTime())"
          /></div
      ></TimePicker>
    </div>
    <div v-if="editShiftTime">
      <TimePicker
        v-model="state.secondTime"
        :label="secondLabel"
        :placeholder="secondPlaceholder"
        ><div class="date-picker">
          <Datepicker
            inputFormat="dd MMM yyyy"
            v-model="state.secondDate"
            :upperLimit="new Date()"
            :lowerLimit="new Date(timesheets.getStartShiftTime())"
          /></div
      ></TimePicker>
    </div>
    <div
      class="__overlap-warning"
      v-if="
        (editShiftTime &&
          isStartTimeOverlappingBreak(
            timesheets.getBreaks(),
            state.firstTime,
            state.firstDate
          )) ||
        (editShiftTime &&
          isEndTimeOverlappingBreak(
            timesheets.getBreaks(),
            state.secondTime,
            state.secondDate
          )) ||
        (editStartTime &&
          isStartTimeOverlappingBreak(
            timesheets.getBreaks(),
            state.firstTime,
            state.firstDate
          ))
      "
    >
      {{ breakOverlapWarning }}
    </div>
    <div
      class="__overlap-warning"
      v-else-if="
        isShiftOverlapping(
          state,
          editStartTime,
          isoDateToSeconds(timesheets.getLastFinishedTimesheetEndTime()!)
        )
      "
    >
      {{ shiftOverlapWarning }}
    </div>
    <CoButton
      data-test-id="save"
      @click="
        editShiftTime
          ? emit(
              'updateShift',
              shiftTimeISO(state.firstTime, state.firstDate),
              shiftTimeISO(state.secondTime, state.secondDate)
            )
          : emit('updateShift', shiftTimeISO(state.firstTime, state.firstDate))
      "
      :disabled="
        isShiftOverlapping(
          state,
          editStartTime,
          isoDateToSeconds(timesheets.getLastFinishedTimesheetEndTime()!)
        ) || (editShiftTime &&
          isStartTimeOverlappingBreak(
            timesheets.getBreaks(),
            state.firstTime,
            state.firstDate
          )) ||
        (editShiftTime &&
          isEndTimeOverlappingBreak(timesheets.getBreaks(), state.secondTime, state.secondDate)) ||
        (editStartTime &&
          isStartTimeOverlappingBreak(timesheets.getBreaks(), state.firstTime, state.firstDate)) ||
        state.emptyField
      "
      type="primary"
      size="large"
      >{{ t('save') }}</CoButton
    >
  </div>
</template>

<style scoped>
.co-button {
  width: 100%;
  margin-top: 8px;
}
.__overlap-warning {
  margin-bottom: 8px;
  line-height: 24px;
  text-align: center;
  color: var(--co-text-attention);
}
</style>
